import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibModal } from '@chewy/kib-overlays-react'

import { usePaymentTransactionStatus } from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/hooks/usePaymentTransactionStatus'
import {
  isTransactionProcessing,
  PaymentFlowState,
} from '~/components/dashboard/invoices/payment/constants/addPaymentDialogV2Constants'

import { usePaymentDialog } from '../context/PaymentDialogProvider'
import { PaymentRequestScreen } from '../screens/PaymentRequestScreen'
import { PaymentSuccessScreen } from '../screens/PaymentSuccessScreen'
import { PaymentSuccessScreenTitle } from '../screens/PaymentSuccessScreenTitle'
import { RecordPaymentScreen } from '../screens/RecordPaymentScreen'
import { roundToTwoDecimals } from '../utils/clientAccountCreditUtils'
import AddPaymentDialogV2Title from './AddPaymentDialogV2Title'
import { PaymentControls } from './PaymentControls'

import '@chewy/kib-overlays-styles/dist/styles/kib-modal.css'
import '@chewy/kib-transition-styles/dist/kib-transition.css'
import styles from './AddPaymentDialogV2.module.scss'

interface AddPaymentDialogV2Props {
  isOpen: boolean
  onClose: () => void
  showStepper?: boolean
}
interface StateConfig {
  controls: JSX.Element
  screen: JSX.Element
  title: string | JSX.Element
}

const AddPaymentDialogV2 = ({
  onClose,
  isOpen,
  showStepper = false,
}: AddPaymentDialogV2Props) => {
  const { dialogState, actions, isButtonActionLoading, invoice, isEstimate } =
    usePaymentDialog()
  const { t } = useTranslation(['Common', 'Dialogs'])
  const [isFormValid, setIsFormValid] = useState(false)
  const [sendToTerminal, setSendToTerminal] = useState(true)

  const { transaction } = usePaymentTransactionStatus(
    dialogState.currentState === PaymentFlowState.PAYMENT_REQUEST
      ? dialogState.paymentData.transactionId || ''
      : '',
  )

  const canCancel =
    dialogState.currentState === PaymentFlowState.PAYMENT_REQUEST &&
    transaction &&
    isTransactionProcessing(transaction) &&
    Boolean(dialogState.paymentData.externalDeviceId) &&
    !dialogState.isCancelling

  const isAccountCreditFullyCovered = useMemo(() => {
    const accountCreditTotal = dialogState.paymentData.accountCreditTotal || 0
    return (
      roundToTwoDecimals(accountCreditTotal) >=
        roundToTwoDecimals(invoice.dueToPay || 0) &&
      dialogState.paymentData.selectedAccountCreditIds &&
      dialogState.paymentData.selectedAccountCreditIds.length > 0
    )
  }, [
    dialogState.paymentData.accountCreditTotal,
    dialogState.paymentData.selectedAccountCreditIds,
    invoice.dueToPay,
  ])

  const handleClose = useCallback(() => {
    actions.resetDialog()
    setIsFormValid(false)
    setSendToTerminal(true)
    onClose()
  }, [actions, onClose])

  useEffect(() => {
    if (
      invoice.dueToPay === 0 &&
      isOpen &&
      dialogState.currentState === PaymentFlowState.RECORD_PAYMENT &&
      !isButtonActionLoading
    ) {
      handleClose()
    }
  }, [
    invoice.dueToPay,
    dialogState.currentState,
    handleClose,
    isOpen,
    isButtonActionLoading,
  ])

  const stateConfig: Record<PaymentFlowState, StateConfig> = useMemo(
    () => ({
      [PaymentFlowState.RECORD_PAYMENT]: {
        screen: (
          <RecordPaymentScreen
            setIsFormValid={setIsFormValid}
            setSendToTerminal={setSendToTerminal}
          />
        ),
        title: (
          <AddPaymentDialogV2Title
            activeStep={1}
            showStepper={showStepper}
            title={
              isEstimate
                ? t('Common:RECORD_DEPOSIT_ACTION')
                : t('Common:RECORD_PAYMENT')
            }
          />
        ),
        controls: (
          <PaymentControls
            disabled={
              (!isFormValid && !isAccountCreditFullyCovered) ||
              isButtonActionLoading
            }
            emphasis="primary"
            isLoading={isButtonActionLoading}
            primaryAction={
              sendToTerminal && !isAccountCreditFullyCovered
                ? () => actions.sendToTerminal(dialogState.paymentData)
                : () => actions.recordPayment(dialogState.paymentData)
            }
            primaryText={
              sendToTerminal && !isAccountCreditFullyCovered
                ? t('Dialogs:ADD_PAYMENT_DIALOG_V2.SEND_TO_PAYMENT_TERMINAL')
                : isEstimate
                  ? t('Common:RECORD_DEPOSIT_ACTION')
                  : t('Common:RECORD_PAYMENT')
            }
          />
        ),
      },
      [PaymentFlowState.PAYMENT_REQUEST]: {
        screen: <PaymentRequestScreen />,
        title: t('Dialogs:ADD_PAYMENT_DIALOG_V2.PAYMENT_REQUEST'),
        controls: (
          <PaymentControls
            align="center"
            disabled={!canCancel}
            emphasis="secondary"
            isLoading={dialogState.isCancelling}
            primaryAction={actions.cancelTransaction}
            primaryText={t('Dialogs:ADD_PAYMENT_DIALOG_V2.CANCEL_TRANSACTION')}
          />
        ),
      },
      [PaymentFlowState.PAYMENT_SUCCESS]: {
        screen: <PaymentSuccessScreen />,
        title: (
          <PaymentSuccessScreenTitle
            title={t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.PAYMENT_SUCCESSFULLY_RECORDED',
            )}
          />
        ),
        controls: (
          <PaymentControls
            emphasis="secondary"
            isLoading={false}
            primaryAction={handleClose}
            primaryText={t('Common:RETURN_TO_INVOICE')}
          />
        ),
      },
    }),
    [
      actions.sendToTerminal,
      actions.recordPayment,
      actions.cancelTransaction,
      dialogState.paymentData,
      t,
      isFormValid,
      sendToTerminal,
      handleClose,
      isButtonActionLoading,
      isAccountCreditFullyCovered,
      canCancel,
      dialogState.isCancelling,
    ],
  )

  const currentConfig = stateConfig[dialogState.currentState]
  const controls = useCallback(() => currentConfig.controls, [currentConfig])
  return (
    <KibModal
      className={styles.addPaymentDialogV2Container}
      controls={controls}
      open={isOpen}
      title={currentConfig.title}
      onClose={handleClose}
    >
      {currentConfig.screen}
    </KibModal>
  )
}

export default AddPaymentDialogV2
